import { AlertColor, Box, Card, CircularProgress, createTheme, ThemeProvider, Tooltip, Typography } from "@mui/material";
import React, { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StarIcon, { StarSlashedIcon } from "../../../assets/svg/StarIcon";
import { WorkspaceIcon } from "../../../assets/svg/WorkspaceIcon";
import { QuickAccessWorkspace } from "../../../model/UserModel";
import { AddWorkspaceLocalStorage } from "../../../pages/PublicLayout/RedirectManager";
import WorkSpaceItemListFileComponentImage from "../../../pages/WorkSpaceItem/WorkSpaceItemListFileComponentImage";
import { createFavorite, deleteFavorite } from "../../../services/favoriteServices";
import { ICreateFavoriteVO, WorkSpaceUserName } from "../../../model/ItemModel";
import { useProfile } from "../../../context/ProfileProvider/useProfile";
import { set } from "lodash";
import { FavoriteType } from "../../../enums/FavoriteType";
import { GetOneItem } from "../../../services/Item/ItemServicesGet";
import ChoicerViwerModal from "../../../pages/WorkSpaceItem/Viwer/ChoicerViwerModal";
import { ConvertWorkspaceToChoicerViwer } from "../../../pages/WorkSpaceItem/Viwer/Convert/ConvertWorkSpaceUserName";

export interface IMiniCard {
  id: string;
  itemId: string | null;
  type: number;
  favoriteName: string;
  extension: string | undefined;
  informationCardType: "quickAccess" | "organizations"
  setPopupMessage: (set: string | undefined) => void;
  setSeverity: (set: AlertColor) => void;
  nameOrg: string;
  setQuickAccessWorkspaces: (set: QuickAccessWorkspace[]) => void;
  quickWorkspaces: QuickAccessWorkspace[];
  setLoadingFavorites: (set: boolean) => void;
  setNeedGetFavorites: (set: boolean) => void;
  workspaceId: string;
}

export const MiniCard: React.FunctionComponent<IMiniCard> = ({
  id,
  itemId,
  type,
  favoriteName,
  extension,
  setPopupMessage,
  setSeverity,
  nameOrg,
  setQuickAccessWorkspaces,
  informationCardType,
  quickWorkspaces,
  setLoadingFavorites,
  setNeedGetFavorites,
  workspaceId
}) => {

  const [isStarSlashedNeeded, setIsStarSlashedNeeded] = useState<boolean>(false);
  const [needColor, setNeedColor] = useState<boolean>(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useProfile();

  const theme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#22638D',
            color: 'white',
            fontSize: '14px',
            height: "30px",
            textAlign: "left",
            font:" normal normal normal 12px/18px Segoe UI",
            letterSpacing: "0.11px",
            opacity: 1
          },
          arrow: {
            color: '#22638D',
          },
        },
      },
    },
  });

  const handleMiniCardInformations = (type: number): { icon: React.ReactNode, name: string } => {
    switch(type) {
      case FavoriteType.Workspace:
        return {
          icon: <WorkspaceIcon 
            height="14"
            width="14"
            color=""
          />,
          name: "Workspace"
        }

      case FavoriteType.Document:
        return {
          icon: <WorkSpaceItemListFileComponentImage 
            extension={extension}
            boxLayout={false}
            isFolder={false}
          />,
          name: t("Minicard.File")
        }

        case FavoriteType.Folder:
          return {
            icon: <WorkSpaceItemListFileComponentImage 
              extension={extension}
              widthIcon="14"
              boxLayout={false}
              isFolder={true}
            />,
            name: t("Minicard.Folder")
          }
      
      default: 
        return {
          icon: <></>,
          name: ""
        }
    }
  }

  const RedirectToWorkspace = () => {
   if (type == FavoriteType.Workspace) {
    navigate(`/${nameOrg}/wsallowed/${itemId}`);
    AddWorkspaceLocalStorage(itemId!);
   }
   else if(type == FavoriteType.Folder) {
    navigate(`/${nameOrg}/wsallowed/${workspaceId}/${itemId}`);
    AddWorkspaceLocalStorage(workspaceId);
   }
   else if(type == FavoriteType.Document) {
    GetOneItem(itemId!)
      .then((newWork) => {
        if (newWork.fatherId) {
          navigate(`/${nameOrg}/wsallowed/${workspaceId}/${newWork.fatherId}?workitem=${itemId}`);
        }else {
          navigate(`/${nameOrg}/wsallowed/${workspaceId}?workitem=${itemId}`);
        }
      });
    AddWorkspaceLocalStorage(workspaceId);
   }
  }

  async function CreateFavorite(input: ICreateFavoriteVO) {
    try {
      setLoadingFavorites(true);
      await createFavorite(input);
      setNeedGetFavorites(true);
    } catch (err: any) {
      setPopupMessage(err.response.data);
      setSeverity("error")
    } finally {
      setLoadingFavorites(false);
    }
  }

  async function DeleteFavorite() {
    try {
      setLoadingFavorites(true);
      await deleteFavorite(nameOrg!, profile?.id!, id);
      setNeedGetFavorites(true);
    } catch (err: any) {
      setPopupMessage(err.response.data);
      setSeverity("error")
    } finally {
      setLoadingFavorites(false);
    }
  }

  return (
    <Box
      onClick={e => {
        e.stopPropagation()
        RedirectToWorkspace();
      }}
      sx={{
        cursor: "pointer"
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "242px",
          border: "1px solid #0000001A",
          borderRadius: "3px",
          position: "relative",
          mr: "10px",
          display: "flex",
          alignItems: "center",
          padding: "11px",
        }}
      >
        <Box
          width="38px"
          height="38px"
          sx={{
            border: "1px solid #0000001A",
            borderRadius: "3px",
          }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr="8px"
        >
          { handleMiniCardInformations(type).icon }
        </Box>
        <Box display="flex" justifyContent="space-between" width="75%">
          <Box>
            <Typography
              sx={{
                textAlign: "left",
                font: "normal normal normal 12px/18px Segoe UI",
                letterSpacing: "0px",
                color: "#2D2D2D",
                opacity: 0.5,
              }}
            >
              { handleMiniCardInformations(type).name }
            </Typography>
            <Tooltip title={favoriteName + (extension ? `.${extension}` : "")} arrow>
              <Typography
                width="150px"
                whiteSpace="nowrap" 
                textOverflow="ellipsis"
                overflow="hidden"
                sx={{
                  textAlign: "left",
                  font: "normal normal 600 14px/21px Segoe UI",
                  letterSpacing: "0px",
                  color: "#2D2D2D",
                  opacity: 1,
                }}
              >
                { favoriteName }{extension ? `.${extension}` : "" }
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{
              top: 0,
              position: "absolute",
              right: 0,
              marginRight: "10px",
              marginTop: "5px",
            }} 
            onClick={async (e) => {
              e.stopPropagation()
              if (informationCardType == "quickAccess") {
                await DeleteFavorite()
              } else {
                await CreateFavorite({
                  extension: extension!,
                  favoriteName: favoriteName,
                  favoriteType: FavoriteType.Workspace,
                  organizationInternalName: nameOrg!,
                  itemId: itemId!
                })
              }
            }}
            onMouseEnter={() => {
              if (informationCardType == "quickAccess") {
                setIsStarSlashedNeeded(true);
                setNeedColor(false);
              }
            }}

            onMouseLeave={() => {
              setIsStarSlashedNeeded(false);
              setNeedColor(false);
            }}
          >
          <ThemeProvider theme={theme}>
            <Tooltip title={informationCardType === "quickAccess" ? t("Minicard.Desfav") : t("Minicard.fav")} arrow>
              <Box
                onMouseEnter={() => {
                  if (informationCardType !== "quickAccess") {
                    setNeedColor(true);
                  }
                }}
                onMouseLeave={() => {
                  if (informationCardType !== "quickAccess") {
                    setNeedColor(false);
                  }
                }}
              >
                {informationCardType === "quickAccess" && isStarSlashedNeeded ? (
                  <StarSlashedIcon width="14" height="14" opacity={1} />
                ) : (
                  <StarIcon
                    width={14}
                    height={14}
                    opacity={
                      informationCardType === "quickAccess"
                        ? 1
                        : needColor
                        ? 1
                        : 0.6
                    }
                    color={
                      informationCardType === "quickAccess"
                        ? "#22638D"
                        : needColor
                        ? "#22638D"
                        : "#000000"
                    }
                  />
                )}
              </Box>
            </Tooltip>
          </ThemeProvider>

          </Box>
        </Box>
      </Card>
    </Box>
  );
};



